import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { TJobOverviewPageRoutes } from './Types';
import { useSafeLocationState } from './useSafeLocationState';
import { firePermJobOverviewTabClicked } from '~/posthogs/events';
import { JobOverviewTab } from '~/posthogs/events/PermJobs/types';

export enum TAB {
  JOB_DETAILS = 'job-details',
  CANDIDATES = 'candidates',
  WORKING_INTERVIEWS = 'onsite-interviews',
}

const tabOrders = [TAB.JOB_DETAILS, TAB.CANDIDATES, TAB.WORKING_INTERVIEWS];

// Map from TAB enum to JobOverviewTab enum for analytics
const tabToAnalyticsMap: Record<TAB, JobOverviewTab> = {
  [TAB.JOB_DETAILS]: JobOverviewTab.JobDetails,
  [TAB.CANDIDATES]: JobOverviewTab.Candidates,
  [TAB.WORKING_INTERVIEWS]: JobOverviewTab.OnsiteInterviews,
};

export const useJobOverview = () => {
  const { postingId, type } = useParams<TJobOverviewPageRoutes>();
  const navigate = useNavigate();
  const state = useSafeLocationState();

  const activeTab = useMemo(
    () => (tabOrders.indexOf(type as TAB) !== -1 ? tabOrders.indexOf(type as TAB) : 0),
    [type],
  );

  const onChangeTab = useCallback(
    (tab: number) => {
      const selectedTab = tabOrders[tab] ?? TAB.JOB_DETAILS;

      // Only proceed if we're actually changing tabs
      if (tab === activeTab) {
        return;
      }

      // Track tab click event
      if (postingId) {
        const analyticsTab = tabToAnalyticsMap[selectedTab];
        firePermJobOverviewTabClicked({
          postingId,
          tabName: analyticsTab,
        });
      }

      navigate(`/permanent-jobs/job-overview/${postingId}/${selectedTab}`, {
        replace: true,
        state,
      });
    },
    [activeTab, navigate, postingId, state],
  );

  return {
    activeTab,
    onChangeTab,
  };
};
